import { FormQuestion } from '../types'

/**
 * Normalización de las opciones de respuesta.
 *
 * Es la ÚNICA fuente de verdad para decidir qué opciones existen: se usa al
 * leer las preguntas de la base de datos, al guardarlas desde el constructor y
 * al renderizarlas en el formulario público. Una opción sin texto (vacía, nula
 * o solo espacios) no es una opción: nunca se muestra ni se persiste.
 *
 * Las descripciones se mantienen alineadas con su opción original, así que al
 * descartar una opción vacía no se desplazan las demás.
 */
export interface NormalizedOptions {
  options: string[]
  descriptions: string[]
}

function isFilled(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== ''
}

/** Opciones y descripciones limpias, conservando su correspondencia. */
export function normalizeOptions(
  rawOptions: unknown,
  rawDescriptions?: unknown,
): NormalizedOptions {
  const source = Array.isArray(rawOptions) ? rawOptions : []
  const descriptions = Array.isArray(rawDescriptions) ? rawDescriptions : []
  const options: string[] = []
  const kept: string[] = []
  source.forEach((option, index) => {
    // Admite tanto `string` como `{ label }`, según cómo se guardó la opción.
    const label =
      typeof option === 'string'
        ? option
        : typeof (option as { label?: unknown })?.label === 'string'
          ? ((option as { label: string }).label)
          : ''
    if (!isFilled(label)) return
    options.push(label.trim())
    const description = descriptions[index]
    kept.push(typeof description === 'string' ? description : '')
  })
  return { options, descriptions: kept }
}

/** Versión de la pregunta lista para renderizar o guardar, sin opciones vacías. */
export function normalizeQuestion(question: FormQuestion): FormQuestion {
  const { options, descriptions } = normalizeOptions(
    question.options,
    question.optionDescriptions,
  )
  return {
    ...question,
    options,
    optionDescriptions: descriptions.some(isFilled) ? descriptions : undefined,
  }
}
